import {
  ArrowRightOutlined,
  CloseOutlined,
  FlagOutlined,
  PlusOutlined,
  RetweetOutlined,
  TrophyOutlined,
} from '@ant-design/icons'
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  InputNumber,
  Row,
  Spin,
  Table,
  Tabs,
  TimePicker,
} from 'antd'
import { ColumnProps } from 'antd/lib/table'
import { addMinutes, format } from 'date-fns'
import { padStart } from 'lodash'
import moment, { Moment } from 'moment'
import * as R from 'ramda'
import React from 'react'
import { DndProvider, DragSource, DropTarget } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { connect as reduxConnect } from 'react-redux'
import { firestoreConnect, isLoaded, withFirebase, withFirestore } from 'react-redux-firebase'
import { withRouter } from 'react-router-dom'
import { withHandlers } from 'recompose'
import { compose } from 'redux'

import * as notificationActions from '../../../actions/notifications'
import { sortByTeamsAndNationalityAndLastName } from '../../../helpers/compare'
import {
  fsCollections,
  makeInBatchWithFirebase,
  saveFlights,
  withFlightsData,
  WithFlightsDataInnerProps,
} from '../../../helpers/firestore'
import { countPlayers, printFlightNumber, withBatchSetStartTime } from '../../../helpers/flights'
import routes from '../../../helpers/routes'
import { emptyRow, playerColumns, renderIfEmpty, skipIfEmpty } from '../../../shared/columns'

import authorize from '../../higher-order/authorize'

import { DEFAULT_INTERVAL, START_TIME_FORMAT } from '../../../constants/flights'
import { playground } from '../../../constants/playgrounds'
import { WithPopupConfirmation } from '../../ActionWithConfirmation'
import DevicePairing from '../../flights/DevicePairing'
import FlightResultsModal from '../../flights/FlightResultsModal'
import StartTimePopover from '../../flights/StartTimePopover'

import { Flight, FlightId } from '../../../schemas/Flight'
import { PlayerId, TournamentPlayer } from '../../../schemas/Player'
import { TenantId } from '../../../schemas/Tenant'
import { FlightSize, Tournament, TournamentId } from '../../../schemas/Tournament'

import './FlightsForm.css'

const EMPTY_ID = 'empty'

const ROW_ITEM_TYPE = 'row'

const FlightRow = ({
  isOver,
  connectDragSource,
  connectDropTarget,
  initialClientOffset,
  children,
  className,
}) => {
  const hoverClassName =
    isOver && initialClientOffset ? `${className} drag-over-highlight` : className

  return connectDragSource(
    connectDropTarget(<tr children={children} className={`${hoverClassName} draggable`} />),
  )
}

const PlayerRow = ({ connectDragSource, className, children }) =>
  connectDragSource(<tr children={children} className={`${className} draggable`} />)

const rowSource = {
  beginDrag: ({ flightIndex, flight, recordIndex, record }) => ({
    flightIndex,
    flight,
    recordIndex,
    record,
  }),
}

const rowTarget = {
  drop(props, monitor) {
    const sourceItem = monitor.getItem()
    const source = {
      flightIndex: sourceItem.flightIndex,
      flight: sourceItem.flight,
      recordIndex: sourceItem.recordIndex,
      record: sourceItem.record,
    }

    const target = {
      flightIndex: props.flightIndex,
      flight: props.flight,
      recordIndex: props.recordIndex,
      record: props.record,
    }

    if (source.flightIndex === target.flightIndex && source.recordIndex === target.recordIndex) {
      return
    }
    props.moveRowAction(source, target)
  },
}

const ConnectedFlightRow = DropTarget(ROW_ITEM_TYPE, rowTarget, (connect, monitor) => ({
  connectDropTarget: connect.dropTarget(),
  isOver: monitor.isOver(),
  sourceClientOffset: monitor.getSourceClientOffset(),
}))(
  DragSource(ROW_ITEM_TYPE, rowSource, (connect, monitor) => ({
    connectDragSource: connect.dragSource(),
    initialClientOffset: monitor.getInitialClientOffset(),
  }))(FlightRow),
)

const ConnectedPlayerRow = DragSource(ROW_ITEM_TYPE, rowSource, (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  initialClientOffset: monitor.getInitialClientOffset(),
}))(PlayerRow)

const getFlightCols = (removePlayerFromFlightAction: (playerId: PlayerId) => () => void) => [
  { ...playerColumns.nationality, render: skipIfEmpty(playerColumns.nationality.render) },
  {
    ...playerColumns.fullName,
    render: renderIfEmpty(
      {
        children: <PlusOutlined />,
        props: { colSpan: 4, className: 'addRow' },
      },
      playerColumns.fullName.render,
    ),
  },
  {
    ...playerColumns.team,
    render: skipIfEmpty(playerColumns.team.render),
    width: '8em',
  },
  {
    title: 'Action',
    key: 'action',
    width: '2em',
    render: skipIfEmpty<Flight>((text, record, index) => {
      return (
        <Button
          icon={<CloseOutlined />}
          shape="circle"
          size="small"
          danger
          onClick={removePlayerFromFlightAction(record.id)}
        />
      )
    }),
  },
]
const playerCols = [playerColumns.nationality, playerColumns.fullName, playerColumns.team]

const addEmptyRow = (flightSize: FlightSize, players: TournamentPlayer[]) =>
  players.length < flightSize ? [...players, emptyRow(EMPTY_ID)] : players

const emptyFlight = (round: number, isFinal: boolean, flightNumber: number, startTime: string) => ({
  players: [],
  round,
  isFinal,
  number: flightNumber,
  startTime,
})

const flightTableComponents = {
  body: {
    row: ConnectedFlightRow,
  },
}

const playerTableComponents = {
  body: {
    row: ConnectedPlayerRow,
  },
}

type FlightCardProps = {
  tenant: TenantId
  tournamentId: TournamentId
  flight: Flight
  flightPlayers: TournamentPlayer[]
  flightSize: FlightSize
  columns: Array<ColumnProps<TournamentPlayer>>
  onRow(record: TournamentPlayer, index: number): any
  changeStartTimeAction(value: Moment | null, dateString: string): void
}

const FlightCard = ({
  tenant,
  tournamentId,
  flight,
  flightPlayers,
  columns,
  onRow,
  flightSize,
  changeStartTimeAction,
}: FlightCardProps) => {
  const isNonEmptyFlight = flightPlayers.length > 0

  const editResults = (
    <FlightResultsModal
      tenant={tenant}
      tournamentId={tournamentId}
      flightId={flight.id}
      playground={playground}
    />
  )

  const enrollDevice = (
    <DevicePairing
      buttonSize="small"
      tenant={tenant}
      tournamentId={tournamentId}
      flightId={flight.id}
    />
  )

  const flightControls = isNonEmptyFlight && (
    <React.Fragment>
      <Button.Group>
        {editResults}
        {enrollDevice}
      </Button.Group>
      <Divider type="vertical" />
      <TimePicker
        size="small"
        format={START_TIME_FORMAT}
        placeholder="Start time"
        value={
          flight.startTime ? (moment(flight.startTime, START_TIME_FORMAT) as FIXME) : undefined
        }
        onChange={changeStartTimeAction}
      />
    </React.Fragment>
  )

  const flightCardTitle = isNonEmptyFlight ? printFlightNumber(flight) : 'New Flight'

  return (
    <Card hoverable className="flight-card" title={flightCardTitle} extra={flightControls}>
      <Table
        className="flight-players"
        showHeader={false}
        pagination={false}
        size="small"
        bordered={false}
        rowKey="id"
        columns={columns}
        dataSource={addEmptyRow(flightSize, flightPlayers)}
        components={flightTableComponents}
        onRow={onRow}
      />
    </Card>
  )
}

type MoveRowAction = (source: RowContext, target: RowContext) => void

type RemoveFromFlightAction = (
  flightIndex: number,
  flight: Flight,
) => (playerId: PlayerId) => () => void

type FlightsProps = {
  tenant: TenantId
  tournamentId: TournamentId
  tournamentPlayers: TournamentPlayer[]
  flights: Flight[]
  flightsPlayers: { [playerId: string]: TournamentPlayer[] }
  flightSize: FlightSize
  round: number
  isFinal: boolean
  moveRowAction: MoveRowAction
  removeFromFlightAction: RemoveFromFlightAction
  defaultInterval?: number
  changeStartTimeAction(flight: Flight): (time: Moment) => void
}

class Flights extends React.Component<FlightsProps, any> {
  render() {
    const {
      tenant,
      tournamentId,
      moveRowAction,
      removeFromFlightAction,
      flightSize,
      round,
      isFinal,
      flights = [],
      flightsPlayers = {},
      tournamentPlayers = [],
      changeStartTimeAction,
      defaultInterval,
    } = this.props

    const sortedFlights = R.sort(R.ascend(R.prop('number')), flights)

    const assignedIds = R.flatten(sortedFlights.map((flight: FIXME) => flight.players))

    const lastStartFlight = R.last(sortedFlights) as FIXME

    const newFlightStartTime =
      defaultInterval &&
      lastStartFlight &&
      lastStartFlight.startTime &&
      format(
        addMinutes(moment(lastStartFlight.startTime, START_TIME_FORMAT).toDate(), defaultInterval),
        START_TIME_FORMAT,
      )

    const newFlightNumber = sortedFlights.length + 1

    const displayedFlights = [
      ...sortedFlights,
      emptyFlight(round, isFinal, newFlightNumber, newFlightStartTime),
    ]

    const displayedPlayers = R.differenceWith(
      (player, id) => player.id === id,
      tournamentPlayers.filter((player) => !player.isDisqualified),
      assignedIds,
    )

    const flightTables = displayedFlights.map((flight: Flight, index: number) => {
      const flightIndex = flights.length - index
      const onFlightRow = (record: TournamentPlayer, recordIndex: number) => ({
        flight,
        flightIndex,
        recordIndex,
        record: record.id,
        moveRowAction,
      })
      const columns = getFlightCols(removeFromFlightAction(flightIndex, flight))

      return (
        <FlightCard
          key={flightIndex}
          tenant={tenant}
          tournamentId={tournamentId}
          flight={flight}
          flightPlayers={flightsPlayers[flight.id] || []}
          flightSize={flightSize}
          changeStartTimeAction={changeStartTimeAction(flight)}
          onRow={onFlightRow}
          columns={columns as FIXME}
        />
      )
    })

    const onPlayerRow = (record: any) => ({ record: record.id }) // required for dnd
    const playersTable = (
      <Table
        rowKey="id"
        pagination={false}
        size="small"
        columns={playerCols}
        dataSource={sortByTeamsAndNationalityAndLastName(displayedPlayers)}
        components={playerTableComponents}
        onRow={onPlayerRow as FIXME}
      />
    )

    const tables = (
      <Row>
        <Col span={12}>
          <h3>Flights</h3>
          <div
            style={{
              boxShadow: '-1px 0px 20px 0px #d2d2d2',
              overflow: 'scroll',
              maxHeight: '450px',
              marginLeft: 10,
              marginRight: 5,
            }}>
            {flightTables}
          </div>
        </Col>
        <Col span={12}>
          <h3>Available Players</h3>
          <div
            style={{
              boxShadow: '-1px 0px 20px 0px #d2d2d2',
              overflow: 'scroll',
              maxHeight: '450px',
              marginLeft: 10,
              marginRight: 5,
            }}>
            {playersTable}
          </div>
        </Col>
      </Row>
    )

    return tables
  }
}

const DragDropFlights = (props: FlightsProps) => (
  <DndProvider backend={HTML5Backend}>
    <Flights {...props} />
  </DndProvider>
)

const rotatePlayers = (flight) =>
  R.over(playersLens, (players) => R.tail([...players, R.head(players)]), flight)

type TournamentFlightsProps = WithFlightsDataInnerProps & {
  tenant: TenantId
  tournamentId: TournamentId
  tournamentPlayers: TournamentPlayer[]
  removeFromFlightAction: RemoveFromFlightAction
  moveRowAction(source: RowContext, target: RowContext): void
  changeStartTimeAction(flight: Flight): (time: Moment) => void
  importPreviousRoundAction(
    sourceRound: number,
    targetRound: number,
    targetIsFinal: boolean,
  ): () => void
  importByCurrentPosition(round: number, isFinal: boolean, ascending: boolean): () => void
  rotatePlayersAction(round: number): () => void
  batchSetStartTimeAction(
    flights: Flight[],
  ): (startTime: Date, interval: number, applyFrom: number) => void
  changeDefaultIntervalAction(defaultInterval: number): void
}

const DEFAULT_FLIGHT_SIZE = 3

const TournamentFlights = ({
  tenant,
  tournament = {} as Tournament,
  flightsDataLoaded,
  tournamentId,
  tournamentPlayers,
  flightsPlayers = {},
  flights,
  moveRowAction,
  removeFromFlightAction,
  changeStartTimeAction,
  importPreviousRoundAction,
  importByCurrentPosition,
  rotatePlayersAction,
  batchSetStartTimeAction,
  changeDefaultIntervalAction,
}: TournamentFlightsProps) => {
  const flightSize = flightsDataLoaded ? tournament.flightSize : DEFAULT_FLIGHT_SIZE
  const finalRound = flightsDataLoaded ? tournament.finalRound : false
  const rounds = (flightsDataLoaded ? tournament.rounds : 0) + (finalRound ? 1 : 0)

  const panes = R.range(1, rounds + 1).map((round) => {
    const isFinal = finalRound && round === rounds
    const tabTitle = isFinal ? (
      <span>
        <FlagOutlined />
        Final
      </span>
    ) : (
      padStart(round.toString(), 2, '0')
    )

    const flightsInRound = (flights || []).filter((flight) => flight.round === round)

    const defaultInterval = (tournament && tournament.defaultInterval) || DEFAULT_INTERVAL

    const buttons = (
      <React.Fragment>
        <Button.Group>
          <WithPopupConfirmation
            title="Are you sure to import flights from previous round?"
            popupPlacement="bottom"
            onConfirmAction={importPreviousRoundAction(round - 1, round, isFinal)}>
            <Button icon={<ArrowRightOutlined />} disabled={round <= 1}>
              From Previous
            </Button>
          </WithPopupConfirmation>
          <WithPopupConfirmation
            title="Are you sure to rotate players in flights?"
            popupPlacement="bottom"
            onConfirmAction={rotatePlayersAction(round)}>
            <Button icon={<RetweetOutlined />}>Rotate Players</Button>
          </WithPopupConfirmation>
          <WithPopupConfirmation
            title="Are you sure to replace all flights with flights by results?"
            popupPlacement="bottom"
            onConfirmAction={importByCurrentPosition(round, isFinal, true)}>
            <Button icon={<TrophyOutlined />} disabled={round <= 1}>
              By Results
            </Button>
          </WithPopupConfirmation>
          <StartTimePopover
            defaultInterval={defaultInterval}
            numberOfFlights={flightsInRound.length}
            onSubmit={batchSetStartTimeAction(flightsInRound)}
          />
        </Button.Group>
        <Divider type="vertical" />
        <div className="ant-form-item-label">
          <label htmlFor="defaultInterval">Default Interval</label>
        </div>
        <InputNumber
          id="defaultInterval"
          onChange={changeDefaultIntervalAction /* TODO triggers update after every stroke */}
          min={1}
          value={defaultInterval}
        />
      </React.Fragment>
    )

    const finalWarning = () => {
      const playersInFinalRound = countPlayers(flightsInRound)

      return (
        tournament.playersInFinalRound !== playersInFinalRound && (
          <Alert
            type="warning"
            message={`Final round has ${playersInFinalRound} players it should have ${tournament.playersInFinalRound} players`}
            showIcon
          />
        )
      )
    }

    return (
      <Tabs.TabPane className="FlightsForm__tabs" tab={tabTitle} key={String(round)}>
        {buttons}
        {isFinal && finalWarning()}
        <DragDropFlights
          tenant={tenant}
          tournamentId={tournamentId}
          tournamentPlayers={tournamentPlayers}
          flightsPlayers={flightsPlayers}
          round={round}
          isFinal={isFinal}
          flights={flightsInRound}
          moveRowAction={moveRowAction}
          removeFromFlightAction={removeFromFlightAction}
          flightSize={flightSize}
          changeStartTimeAction={changeStartTimeAction}
          defaultInterval={defaultInterval}
        />
      </Tabs.TabPane>
    )
  })

  const tabs = (
    <div className="FlightsForm">
      <h3>Manage Flights</h3>
      <Tabs tabPosition="left">{panes}</Tabs>
    </div>
  )

  return flightsDataLoaded && isLoaded(tournamentPlayers) ? tabs : <Spin>{tabs}</Spin>
}

const playersLens = R.lensProp('players')

const withoutEmptyRecords = (flight) =>
  R.over(playersLens, (players) => players.filter((record) => record !== EMPTY_ID), flight)

const updateRecord = (flight, recordIndex, record) => {
  const recordLens = R.compose(playersLens, R.lensIndex(recordIndex)) as FIXME

  return withoutEmptyRecords(
    R.view(recordLens, flight)
      ? R.set(recordLens, record, flight)
      : R.over(playersLens, (players) => [...players, record], flight),
  )
}

type RowContext = {
  flightIndex: number
  flight?: Flight
  recordIndex: number
  record: PlayerId
}

const makeMoveRowAction: ({ saveFlightsAction }) => MoveRowAction = ({ saveFlightsAction }) => (
  source: RowContext,
  target: RowContext,
) => {
  if (source.flightIndex === target.flightIndex) {
    saveFlightsAction(
      updateRecord(
        updateRecord(source.flight, source.recordIndex, target.record),
        target.recordIndex,
        source.record,
      ),
    )
  } else if (source.flight && target.flight) {
    saveFlightsAction(
      updateRecord(source.flight, source.recordIndex, target.record),
      updateRecord(target.flight, target.recordIndex, source.record),
    )
  } else {
    saveFlightsAction(updateRecord(target.flight, target.recordIndex, source.record))
  }
}

const makeRemoveFromFlightAction: ({ saveFlightsAction }) => RemoveFromFlightAction = ({
  saveFlightsAction,
}) => (flightIndex: number, flight: Flight) => (playerId: PlayerId) => () =>
  saveFlightsAction(
    R.over(playersLens, (players) => players.filter((record) => record !== playerId), flight),
  )

type WithReplaceFlights = {
  replaceFlights(originalFlights: Flight[], newFlights: Flight[]): Promise<{}>
}

const makeImportPreviousRoundAction = ({
  replaceFlights,
  flights = [],
}: TournamentFlightsProps & WithReplaceFlights) => (
  sourceRound: number,
  targetRound: number,
  targetIsFinal: boolean,
) => () => {
  const sourceFlights = flights.filter((flight) => flight.round === sourceRound)
  const originalFlights = flights.filter((flight) => flight.round === targetRound)
  const newFlights = sourceFlights.map((flight) =>
    (R.compose<FIXME, FIXME, FIXME>(
      R.assoc('round', targetRound),
      R.assoc('isFinal', targetIsFinal),
      ((o) => R.omit<FIXME, FIXME>(['id', 'startTime'], o)) as FIXME,
    ) as FIXME)(flight as FIXME),
  )

  return replaceFlights(originalFlights, newFlights as FIXME)
}

const makeImportByCurrentPosition = ({
  replaceFlights,
  flights = [],
  playerIdsByPositionInMain = [],
  tournament,
}: TournamentFlightsProps & WithReplaceFlights) => (
  round: number,
  isFinal: boolean,
  ascending: boolean,
) => () => {
  if (!tournament) {
    return undefined
  }
  const { flightSize } = tournament

  const playerCountToAssign = isFinal
    ? tournament.playersInFinalRound
    : playerIdsByPositionInMain.length
  const slicedPlayerIdsByPositionInMain = playerIdsByPositionInMain.slice(0, playerCountToAssign)

  const playerIds = ascending
    ? R.reverse(slicedPlayerIdsByPositionInMain)
    : slicedPlayerIdsByPositionInMain
  const numberOfFlights = tournament
    ? Math.ceil(slicedPlayerIdsByPositionInMain.length / flightSize)
    : 0
  const originalFlights = flights.filter((flight) => flight.round === round)
  const shift = (flightSize - (playerIds.length % flightSize)) % flightSize
  const newFlights = R.range(0, numberOfFlights).map((num) => ({
    number: num + 1,
    players: playerIds.slice(num ? num * flightSize - shift : 0, (num + 1) * flightSize - shift),
    round,
    isFinal,
  }))

  return replaceFlights(originalFlights, newFlights as FIXME)
}

type WithSaveFlightsAction = {
  saveFlightsAction(...flights: Flight[]): Promise<{}>
}

const makeRotatePlayersAction = ({
  saveFlightsAction,
  flights = [],
}: TournamentFlightsProps & WithSaveFlightsAction) => (round: number) => () => {
  const sourceFlights = flights.filter((flight) => flight.round === round)

  return saveFlightsAction(...sourceFlights.map((flight) => rotatePlayers(flight)))
}

const makeBatchSetStartTimeAction = ({ saveFlightsAction }: WithSaveFlightsAction) => (
  flights: Flight[],
) => (startTime: Date, interval: number, applyFrom: number) =>
  saveFlightsAction(...withBatchSetStartTime(flights)(startTime, interval, applyFrom))

const makeEditResultsAction = ({ tenant, tournamentId, history }) => (flightId: FlightId) => () =>
  history.push(routes.manageFlightResults(tenant, tournamentId, flightId))

const makeChangeStartTimeAction = ({ saveFlightsAction }) => (flight: Flight) => (time: Moment) =>
  saveFlightsAction({ ...flight, startTime: time && time.format(START_TIME_FORMAT) })

const makeChangeDefaultIntervalAction = ({ firestore, tenant, tournamentId, tournament }) => (
  defaultInterval: number,
) =>
  firestore.set(
    { collection: fsCollections.tournaments(tenant), doc: tournamentId },
    { ...tournament, defaultInterval: defaultInterval || DEFAULT_INTERVAL },
  )

export default compose<FIXME>(
  authorize('admin'),
  withRouter,
  withFirebase,
  withFirestore,
  firestoreConnect<FIXME>(({ tenant, tournamentId }) => [
    { collection: fsCollections.tournamentPlayers(tenant, tournamentId) },
  ]),
  reduxConnect(({ firestore }: FIXME, { tenant, tournamentId }: FIXME) => ({
    tournamentPlayers: firestore.ordered[fsCollections.tournamentPlayers(tenant, tournamentId)],
  })),
  withFlightsData,
  withHandlers({
    saveFlightError: ({ dispatch }: FIXME) => (err) => {
      // tslint:disable-next-line:no-console
      console.error(err)
      dispatch(notificationActions.notifyFailure('Failed to save flight data'))
    },
    inBatchBound: makeInBatchWithFirebase,
  }),
  withHandlers({
    saveFlightsAction: ({ inBatchBound, saveFlightError, tenant, tournamentId, flights }) => (
      ...modifiedFlights: Flight[]
    ) =>
      inBatchBound((firestore, batch) => {
        saveFlights({ tenant, tournamentId, flights })(firestore, batch)(...modifiedFlights)
      }).catch(saveFlightError),
    replaceFlights: ({ inBatchBound, saveFlightError, tenant, tournamentId }) => (
      originalFlights: Flight[],
      newFlights: Flight[],
    ) =>
      inBatchBound((firestore, batch) => {
        const collection = firestore.collection(
          fsCollections.tournamentFlights(tenant, tournamentId),
        )
        originalFlights.forEach((flight) => batch.delete(collection.doc(flight.id)))
        newFlights.forEach((flight) => batch.set(collection.doc(), flight))
      }).catch(saveFlightError),
  }),
  withHandlers<any, any>({
    moveRowAction: makeMoveRowAction,
    removeFromFlightAction: makeRemoveFromFlightAction,
    importPreviousRoundAction: makeImportPreviousRoundAction,
    importByCurrentPosition: makeImportByCurrentPosition,
    rotatePlayersAction: makeRotatePlayersAction,
    editResultsAction: makeEditResultsAction,
    changeStartTimeAction: makeChangeStartTimeAction,
    batchSetStartTimeAction: makeBatchSetStartTimeAction,
    changeDefaultIntervalAction: makeChangeDefaultIntervalAction,
  }),
)(TournamentFlights) // TODO improve typing



// WEBPACK FOOTER //
// ./src/components/tournament/management/FlightsForm.tsx